import React, { useState, useEffect } from 'react';
import { contactAPI } from '../../services/api';
import { Trash2, Phone, Mail, MapPin } from 'lucide-react';
import { motion } from 'framer-motion';

const Contact = () => {
    const [inquiries, setInquiries] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetchInquiries();
    }, []);

    const fetchInquiries = async () => {
        try {
            const response = await contactAPI.getAll();
            setInquiries(response.data);
        } catch (error) {
            console.error("Error fetching inquiries:", error);
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (id) => {
        if (window.confirm('Are you sure you want to delete this inquiry?')) {
            try {
                await contactAPI.delete(id);
                setInquiries(inquiries.filter(inquiry => inquiry.id !== id));
            } catch (error) {
                console.error("Error deleting inquiry:", error);
                alert("Failed to delete inquiry");
            }
        }
    };

    return (
        <div className="pt-24 min-h-screen bg-gray-50 px-6 pb-12 font-sans">
            <div className="max-w-7xl mx-auto">
                <div className="flex justify-between items-center mb-8">
                    <h1 className="text-3xl font-bold text-gray-900">Inquiries</h1>
                    <span className="text-gray-500 bg-white px-4 py-2 rounded-full shadow-sm text-sm font-medium">
                        Total: {inquiries.length}
                    </span>
                </div>

                {loading ? (
                    <div className="text-center py-20 flex justify-center">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
                    </div>
                ) : inquiries.length === 0 ? (
                    <div className="text-center py-20 bg-white rounded-2xl shadow-sm">
                        <div className="text-gray-400 mb-4">
                            <Mail className="h-16 w-16 mx-auto opacity-20" />
                        </div>
                        <p className="text-xl text-gray-500">No inquiries yet.</p>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {inquiries.map((inquiry) => (
                            <motion.div
                                key={inquiry.id}
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                className="bg-white rounded-2xl shadow-md overflow-hidden hover:shadow-lg transition-all border border-gray-100"
                            >
                                <div className="p-6">
                                    <div className="flex justify-between items-start mb-4">
                                        <div>
                                            <h3 className="text-xl font-bold text-gray-900">{inquiry.name}</h3>
                                            <p className="text-xs text-gray-400 mt-1">
                                                {new Date(inquiry.createdAt).toLocaleString()}
                                            </p>
                                        </div>
                                        <button
                                            onClick={() => handleDelete(inquiry.id)}
                                            className="text-gray-400 hover:text-red-500 p-2 hover:bg-red-50 rounded-full transition-colors"
                                            title="Delete Inquiry"
                                        >
                                            <Trash2 size={18} />
                                        </button>
                                    </div>

                                    <div className="space-y-3 mb-6">
                                        <div className="flex items-center text-gray-600 text-sm">
                                            <Phone size={16} className="mr-3 text-green-600" />
                                            {inquiry.phone}
                                        </div>
                                    </div>

                                    <div className="bg-gray-50 rounded-xl p-4 text-gray-700 text-sm italic leading-relaxed relative">
                                        <div className="absolute top-2 left-2 text-gray-300 text-4xl leading-none select-none">"</div>
                                        <p className="relative z-10 pl-2">{inquiry.message}</p>
                                    </div>
                                </div>
                            </motion.div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

export default Contact;
